import { stripLangPrefix } from "@/i18n/language";
import type { Lang } from "@/i18n/language";
import {
  DEFAULT_OG_IMAGE,
  BUSINESS,
  NOINDEX_ROUTES,
  defaultSeo,
  seoMap,
} from "@/lib/seoData";
import type { SeoEntry } from "@/lib/seoData";
import { SITE_URL } from "@/lib/seoData";

// Daten unverändert weiterreichen, damit bestehende Importe gültig bleiben.
export {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  BUSINESS,
  TWITTER_HANDLE,
  NOINDEX_ROUTES,
  NON_SITEMAP_ROUTES,
  defaultSeo,
  seoMap,
} from "@/lib/seoData";
export type { SeoMeta, SeoEntry } from "@/lib/seoData";

export function getSeo(path: string): SeoEntry {
  if (seoMap[path]) return seoMap[path];
  const segments = path.split("/").filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const key = `/${segments.slice(0, i + 1).join("/")}`;
    if (seoMap[key]) return seoMap[key];
  }
  return defaultSeo;
}

/** true, wenn die Route nicht in den Suchindex gehört (Formulare, Voucher). */
export function isNoindexRoute(path: string): boolean {
  return NOINDEX_ROUTES.includes(path);
}

/** Liefert das Basis-Präfix der Deployment-URL, z. B. "/web" oder "" (Root). */
export function getBasePrefix(): string {
  const base = __BASE_PATH__;
  if (!base || base === "/") return "";
  return base.replace(/\/+$/, "");
}

/** Liefert den App-relativen Pfad (ohne Basis-Präfix), z. B. "/villas" oder "/en/villas". */
export function getAppPath(): string {
  const full = window.location.pathname;
  const base = getBasePrefix();
  let app = full;
  if (base && full.startsWith(base)) {
    app = full.slice(base.length);
  }
  return app || "/";
}

/**
 * Baut die absolute Produktions-URL für einen App-Pfad in der gewünschten
 * Sprache (immer auf Basis von SITE_URL – unabhängig davon, unter welcher
 * Domain die Seite gerade erreichbar ist).
 */
export function buildUrl(appPath: string, lang: Lang): string {
  const stripped = stripLangPrefix(appPath || "/");
  const clean = stripped === "/" ? "" : stripped.replace(/\/+$/, "");
  if (lang === "en") {
    return `${SITE_URL}/en${clean}`;
  }
  return `${SITE_URL}${clean || "/"}`;
}

/** Kurzer, sprachgerechter Name einer Route für Breadcrumbs. */
function breadcrumbName(path: string, lang: Lang): string {
  const title = getSeo(path)[lang].title;
  return title.split("|")[0].split("–")[0].trim();
}

/** Baut eine BreadcrumbList für eine beliebige Unterseite. */
function buildBreadcrumb(stripped: string, lang: Lang): object {
  const segments = stripped.split("/").filter(Boolean);
  const itemListElement: object[] = [
    {
      "@type": "ListItem",
      position: 1,
      name: lang === "de" ? "Startseite" : "Home",
      item: `${SITE_URL}/`,
    },
  ];
  segments.forEach((_, index) => {
    const partial = `/${segments.slice(0, index + 1).join("/")}`;
    itemListElement.push({
      "@type": "ListItem",
      position: index + 2,
      name: breadcrumbName(partial, lang),
      item: buildUrl(partial, lang),
    });
  });
  return { "@type": "BreadcrumbList", itemListElement };
}

/** Villen-Detailseiten, für die ein eigener Unterkunfts-Knoten ausgegeben wird. */
const VILLA_ACCOMMODATION_PAGES = [
  "/villa-frangipani",
  "/villa-bougainvillea",
  "/villa-hibiscus",
];

/**
 * Erzeugt das vollständige strukturierte Datenpaket (schema.org @graph) für
 * die aktuelle Route – inkl. lokalen Betriebsdaten, Breadcrumbs und
 * seitenspezifischen Knoten (Touren, Hochzeiten).
 */
export function buildJsonLd(appPath: string, lang: Lang): object {
  const stripped = stripLangPrefix(appPath || "/");
  const entry = getSeo(stripped);
  const meta = entry[lang];
  const pageImage = entry.image || DEFAULT_OG_IMAGE;

  const graph: object[] = [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: BUSINESS.name,
      url: `${SITE_URL}/`,
      logo: DEFAULT_OG_IMAGE,
      image: DEFAULT_OG_IMAGE,
      email: BUSINESS.email,
      telephone: BUSINESS.phone,
      sameAs: BUSINESS.sameAs,
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: BUSINESS.phone,
          contactType: "reservations",
          areaServed: "ID",
          availableLanguage: ["German", "English", "Indonesian"],
        },
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: BUSINESS.name,
      inLanguage: ["de", "en"],
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
    {
      "@type": "LodgingBusiness",
      "@id": `${SITE_URL}/#lodging`,
      name: BUSINESS.name,
      description: BUSINESS.description,
      url: `${SITE_URL}/`,
      image: [DEFAULT_OG_IMAGE],
      telephone: BUSINESS.phone,
      email: BUSINESS.email,
      priceRange: BUSINESS.priceRange,
      currenciesAccepted: "USD, EUR, IDR",
      checkinTime: "15:00",
      checkoutTime: "12:00",
      address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS.street,
        addressLocality: BUSINESS.city,
        addressRegion: BUSINESS.region,
        postalCode: BUSINESS.postalCode,
        addressCountry: BUSINESS.country,
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: BUSINESS.lat,
        longitude: BUSINESS.lng,
      },
      sameAs: BUSINESS.sameAs,
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          opens: "00:00",
          closes: "23:59",
        },
      ],
    },
    {
      "@type": "WebPage",
      "@id": `${buildUrl(stripped, lang)}#webpage`,
      url: buildUrl(stripped, lang),
      name: meta.title,
      description: meta.description,
      inLanguage: lang,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#lodging` },
      primaryImageOfPage: { "@type": "ImageObject", url: pageImage },
    },
  ];

  if (stripped !== "/") {
    graph.push(buildBreadcrumb(stripped, lang));
  }

  // Eigener Unterkunfts-Knoten für unsere drei Villen.
  if (VILLA_ACCOMMODATION_PAGES.includes(stripped)) {
    graph.push({
      "@type": "Accommodation",
      "@id": `${buildUrl(stripped, lang)}#accommodation`,
      name: breadcrumbName(stripped, lang),
      description: meta.description,
      url: buildUrl(stripped, lang),
      image: [pageImage],
      containedInPlace: { "@id": `${SITE_URL}/#lodging` },
      address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS.street,
        addressLocality: BUSINESS.city,
        addressRegion: BUSINESS.region,
        postalCode: BUSINESS.postalCode,
        addressCountry: BUSINESS.country,
      },
    });
  }

  // Übersichtsseite der Villen als ItemList ausgeben.
  if (stripped === "/villas") {
    graph.push({
      "@type": "ItemList",
      "@id": `${buildUrl(stripped, lang)}#villa-list`,
      name: breadcrumbName(stripped, lang),
      itemListElement: VILLA_ACCOMMODATION_PAGES.map((villa, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: breadcrumbName(villa, lang),
        url: buildUrl(villa, lang),
      })),
    });
  }

  if (stripped.startsWith("/tour/")) {
    graph.push({
      "@type": "TouristTrip",
      "@id": `${buildUrl(stripped, lang)}#trip`,
      name: breadcrumbName(stripped, lang),
      description: meta.description,
      url: buildUrl(stripped, lang),
      provider: { "@id": `${SITE_URL}/#organization` },
    });
  }

  if (stripped.startsWith("/weddings")) {
    graph.push({
      "@type": "Service",
      "@id": `${buildUrl(stripped, lang)}#wedding-service`,
      serviceType: "Wedding planning and venue",
      name: breadcrumbName(stripped, lang),
      description: meta.description,
      url: buildUrl(stripped, lang),
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: { "@type": "Country", name: "Indonesia" },
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}